import { useCallback, useState } from 'react'
import { ContentfulMarkdown } from '@utils/contentful/ContentfulMarkdown'
import { ContentfulNextJsImage } from '@components/common/ContentfulImage'
import {
  Accordion,
  AccordionItem,
  IAccordionItemConfig,
} from '@noissue-ui-kit/oldBranding/accordion'
import { IImage } from '@utils/contentful/contentfulImage'
import cn from 'classnames'

export interface IServiceDetail {
  label: string
  labelIcon?: IImage
  content: string
}

interface IServiceDetailsSection {
  serviceDetails: IServiceDetail[]
}

export function ServiceDetailsSection({
  serviceDetails,
}: IServiceDetailsSection) {
  const [accordionState, setAccordionState] =
    useState<IAccordionItemConfig[]>(null)

  if (!serviceDetails?.length) {
    return null
  }

  const renderTitle = useCallback(({ image, label }) => {
    if (!image && !label) {
      return null
    }

    return (
      <div className="flex items-center">
        {image && image.imageUrl && (
          <ContentfulNextJsImage
            src={image.imageUrl}
            alt={image.imageTitle}
            width={24}
            height={24}
            objectFit="contain"
            className="mr-5"
            loading="lazy"
          />
        )}

        {label && (
          <ContentfulMarkdown overrideClass="text-black text-xl font-normal m-0">
            {label}
          </ContentfulMarkdown>
        )}
      </div>
    )
  }, [])

  return (
    <Accordion
      initialMode="hideAll"
      className="my-10"
      onChange={setAccordionState}
    >
      {serviceDetails?.map((x, index) => (
        <AccordionItem
          key={x.label || x.labelIcon?.imageTitle}
          label={renderTitle({ image: x.labelIcon, label: x.label })}
          className={cn(
            'bg-pebble border-2 border-pebble rounded-lg px-5 py-4 mb-5 select-none hover:border-black-40',
            {
              'border-black-40': accordionState?.[index]?.opened,
            }
          )}
        >
          <div className="py-4 px-6 bg-pebble rounded-lg mb-7">
            <ContentfulMarkdown
              overrideClass="text-black"
              overrideStyles={{
                a: ({ children, href }) => (
                  <a
                    className="pointer-events-auto text-black text-xl underline"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {` ${children} `}
                  </a>
                ),
                h2: ({ children }) => (
                  <h4 className="text-black text-xl font-semibold mb-2">
                    {children}
                  </h4>
                ),
              }}
            >
              {x.content}
            </ContentfulMarkdown>
          </div>
        </AccordionItem>
      ))}
    </Accordion>
  )
}
